/**
 * ChauparDice — 3 rectangular dice (pāsā) component
 *
 * Features:
 * - 3 rectangular dice with rolling animation
 * - Individual die selection for assignment to pawns
 * - Triple detection visual indicator
 * - Used/unused state display
 */

import React, { useState, useEffect } from 'react';
import './ChauparDice.css';

interface ChauparDiceProps {
  values: [number, number, number] | null;
  isRolling: boolean;
  onRoll: () => void;
  canRoll: boolean;
  usedDice: boolean[];
  onDieSelect?: (dieIndex: number) => void;
  selectedDie?: number | null;
  isTriple?: boolean;
  bonusValue?: number;
}

const DIE_FACES: Record<number, string> = {
  1: '⚀', 2: '⚁', 3: '⚂', 4: '⚃', 5: '⚄', 6: '⚅',
};

export const ChauparDice: React.FC<ChauparDiceProps> = ({
  values,
  isRolling,
  onRoll,
  canRoll,
  usedDice,
  onDieSelect,
  selectedDie,
  isTriple,
  bonusValue,
}) => {
  const [animatingDice, setAnimatingDice] = useState<[number, number, number]>([1, 1, 1]);

  useEffect(() => {
    if (isRolling) {
      const interval = setInterval(() => {
        setAnimatingDice([
          Math.floor(Math.random() * 6) + 1,
          Math.floor(Math.random() * 6) + 1,
          Math.floor(Math.random() * 6) + 1,
        ]);
      }, 100);

      return () => clearInterval(interval);
    }
  }, [isRolling]);

  const displayValues = isRolling ? animatingDice : (values || [0, 0, 0]);

  return (
    <div className="chaupar-dice-container" role="region" aria-label="Dice area">
      {/* Dice display */}
      <div className="chaupar-dice-row">
        {[0, 1, 2].map((dieIndex) => {
          const value = displayValues[dieIndex];
          const isUsed = usedDice[dieIndex];
          const isSelected = selectedDie === dieIndex;

          return (
            <button
              key={dieIndex}
              className={`chaupar-die ${isRolling ? 'rolling' : ''} ${isUsed ? 'used' : ''} ${isSelected ? 'selected' : ''} ${value === 1 ? 'entry-die' : ''}`}
              onClick={() => !isUsed && !isRolling && onDieSelect?.(dieIndex)}
              disabled={isUsed || isRolling || !values}
              aria-label={`Die ${dieIndex + 1}: ${value}${isUsed ? ' (used)' : ''}${isSelected ? ' (selected)' : ''}`}
              tabIndex={isUsed ? -1 : 0}
            >
              <span className="die-face">{value > 0 ? DIE_FACES[value] || value : '?'}</span>
              <span className="die-value">{value > 0 ? value : '?'}</span>
            </button>
          );
        })}
      </div>

      {/* Triple indicator */}
      {isTriple && !isRolling && (
        <div className="triple-indicator" aria-live="polite">
          <span className="triple-icon">✨</span>
          <span className="triple-text">TRIPLE! Extra Turn!</span>
          {bonusValue && bonusValue > 0 && (
            <span className="bonus-text">+{bonusValue} Bonus</span>
          )}
        </div>
      )}

      {/* Roll button */}
      <button
        className={`chaupar-roll-btn ${canRoll ? 'can-roll' : ''} ${isRolling ? 'rolling' : ''}`}
        onClick={onRoll}
        disabled={!canRoll || isRolling}
        aria-label={isRolling ? 'Rolling dice...' : 'Roll dice'}
      >
        {isRolling ? '🎲 Rolling...' : '🎲 Roll Pāsā'}
      </button>

      {/* Instructions */}
      {values && !usedDice.every(Boolean) && (
        <p className="dice-instructions" aria-live="polite">
          Select a die, then click a pawn to move
        </p>
      )}
    </div>
  );
};

export default ChauparDice;
